import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate, useLocation } from "react-router-dom";
import GoodStandingPaymentUtils from "../utils/goodStandingPaymentUtils";
import "../styles/GoodStandingPayment.css";

const GoodStandingPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
const [paymentCompleted, setPaymentCompleted] = useState(false);

  React.useEffect(() => {
    const fetchFormData = async () => {
      try {
        setFetchingData(true);
        
        // Get form data from location state
        if (location.state?.formData) {
          const formId = location.state.formData._id;
          
          // Fetch updated data with payment info from backend
          const response = await GoodStandingPaymentUtils.getGoodStandingFormById(formId);
          
          if (response.success) {
            setFormData(response.data);
            
            // Set payment amount from backend or use default
            const amount = response.data.payment_info?.amount_rupees || response.data.amount || 500;
            setPaymentAmount(amount);
          } else {
            throw new Error(response.message || 'Failed to fetch  Professional status form details');
          }
        } else {
          setError("No  Professional status form data found. Please submit the form first.");
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        setError(GoodStandingPaymentUtils.getErrorMessage(error));
      } finally {
        setFetchingData(false);
      }
    };

    fetchFormData();
  }, [location.state]);

  const handlePayment = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Load Razorpay script
      await GoodStandingPaymentUtils.loadRazorpayScript();

      // Create order
      const orderData = await GoodStandingPaymentUtils.createGoodStandingOrder(
        formData._id,
        paymentAmount
      );

      // Open Razorpay checkout
      await GoodStandingPaymentUtils.openGoodStandingCheckout({
        orderData,
        formData,
        onSuccess: async (paymentResponse) => {
          try {
            await verifyPayment(paymentResponse);
          } catch (error) {
            setError("Payment verification failed: " + error.message);
            setLoading(false);
          }
        },
        onFailure: (error) => {
          setError(error.message);
          setLoading(false);
        }
      });
    } catch (error) {
      setError(GoodStandingPaymentUtils.getErrorMessage(error));
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentResponse) => {
    try {
      if (!GoodStandingPaymentUtils.validatePaymentResponse(paymentResponse)) {
        throw new Error("Invalid payment response");
      }

      const result = await GoodStandingPaymentUtils.verifyGoodStandingPayment(
        formData._id,
        {
          ...paymentResponse,
          amount_paise: paymentAmount * 100,
          amount_rupees: paymentAmount
        }
      );

     if (result.success) {
  setSuccess("Payment successful! Your  Professional status request has been processed.");
  setPaymentCompleted(true); // ✅ IMPORTANT
  setLoading(false);

  setTimeout(() => {
    navigate('/professional-status-payment-success', {
      state: {
        formData,
        paymentDetails: {
          orderId: paymentResponse.razorpay_order_id,
          paymentId: paymentResponse.razorpay_payment_id,
          amountInRupees: paymentAmount
        }
      }
    });
  }, 2000);
}
 else {
        throw new Error(result.message || 'Payment verification failed');
      }
    } catch (error) {
      setError("Payment verification failed: " + GoodStandingPaymentUtils.getErrorMessage(error));
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container good-standing-payment-content-container">
          <section className="good-standing-payment-section">
            <Container>
              <h2 className="text-center good-standing-payment-heading mb-4">
                 Professional status Payment
              </h2>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              {/* Form Details */}
              <Card className="mb-4 shadow-sm good-standing-payment-card">
                <Card.Header>
                  <h4 className="mb-0"> Professional status Form Details</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Authority Name:</strong> {formData.authorityName}</p>
                      <p><strong>AIOTA Membership Number:</strong> {formData.aiotaMembershipNumber}</p>
                      <p><strong>ACOT/IOTR Number:</strong> {formData.acotIotrNumber}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Submission Date:</strong> {new Date(formData.createdAt).toLocaleDateString()}</p>
                      <p><strong>Status:</strong> <span className="text-info">Pending Payment</span></p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Payment Information */}
              <Card className="mb-4 shadow-sm good-standing-payment-card">
                <Card.Header>
                  <h4 className="mb-0">Payment Information</h4>
                </Card.Header>
                <Card.Body>
                  <div className="good-standing-payment-summary">
                    <div className="summary-row">
                      <span> Professional status Processing Fee (including 18% GST):</span>
                      <span>₹{paymentAmount.toLocaleString()}</span>
                    </div>
                    <div className="summary-row total">
                      <span><strong>Total Amount:</strong></span>
                      <span><strong>₹{paymentAmount.toLocaleString()}</strong></span>
                    </div>
                  </div>
                  
                  <div className="good-standing-payment-actions mt-4">
                   <div className="d-flex justify-content-center">
  <Button
    variant="primary"
    size="lg"
    className="button-ad-submission-payment d-flex align-items-center justify-content-center"
    onClick={handlePayment}
disabled={loading || paymentCompleted}
  >
    {loading ? (
      <>
        <Spinner
          as="span"
          animation="border"
          size="sm"
          role="status"
          aria-hidden="true"
          className="me-2"
          style={{
            width: "14px",
            height: "14px",
            borderWidth: "2px",
          }}
        />
        <span>Processing Payment...</span>
      </>
    ) : (
      `Pay ₹${paymentAmount.toLocaleString()}`
    )}
  </Button>
</div>

                  </div>
                </Card.Body>
              </Card>

              {/* Important Notes */}
              <Card className="mb-4 shadow-sm good-standing-payment-card">
                <Card.Header>
                  <h4 className="mb-0">Important Notes</h4>
                </Card.Header>
                <Card.Body>
                  <ul className="good-standing-payment-notes">
                    <li>Payment amount includes 18% GST as per government regulations.</li>
                    <li>Payment must be completed to process your  Professional status request.</li>
                    <li> Professional status certificate will be generated after successful payment.</li>
                    <li>You will receive the certificate via email once processing is complete.</li>
                    <li>All payments are processed securely through Razorpay.</li>
                    <li>For any payment issues, please contact the AIOTA office.</li>
                  </ul>
                </Card.Body>
              </Card>

              {/* Action Buttons */}
              <Row className="d-flex justify-content-center mb-4">
                <Col md={6} className="text-center">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/professional-status-form')}
                    className="me-3"
                  >
                    Back to Form
                  </Button>
                 
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </>
  );
};

export default GoodStandingPayment;
